import React from 'react';
import { School, Users, GraduationCap, Heart, BookOpen, ShieldCheck, Activity, TrendingUp, AlertTriangle, CheckCircle2, Clock, Server, RefreshCw, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { QualityRing, ProgressBar, containerVariants, itemVariants } from './dashboard/DashboardCharts';
import { useDashboardData } from './dashboard/useDashboardData';
export function DashboardPage({
  onNavigate
}: {
  onNavigate?: (view: string) => void;
}) {
  const {
    stats,
    contentStats,
    pipelineLoading,
    strictModeOn,
    handleRefreshPipeline,
    passCount,
    warnCount,
    failCount,
    avgScore,
    totalValidated,
    systemHealth,
    recentActivity
  } = useDashboardData();
  return <PageLayout title="Dashboard" subtitle="Overzicht van het Meester djay platform" actions={<ConnectionBadge status="live" data-id="element-3683" />} data-id="element-3682">
      {/* Section 1: Platform Overview */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-3684">
        <motion.div variants={itemVariants} data-id="element-3685">
          <StatCard label="Leerlingen" value={stats.totalLearners} icon={<Users className="w-4 h-4 text-blue-500" data-id="element-3687" />} data-id="element-3686" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3688">
          <StatCard label="Docenten" value={stats.totalTeachers} icon={<GraduationCap className="w-4 h-4 text-indigo-500" data-id="element-3690" />} data-id="element-3689" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3691">
          <StatCard label="Ouders" value={stats.totalParents} icon={<Heart className="w-4 h-4 text-rose-500" data-id="element-3693" />} data-id="element-3692" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3694">
          <StatCard label="Scholen" value={stats.totalSchools} icon={<School className="w-4 h-4 text-purple-500" data-id="element-3696" />} data-id="element-3695" />
        </motion.div>
      </motion.div>

      {/* Section 2: Content & Quality */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-3697">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-3698">
          Content & Kwaliteit
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3699">
          {/* Left: Content Stats */}
          <div className="space-y-4" data-id="element-3700">
            <div className="flex items-center justify-between" data-id="element-3701">
              <div className="flex items-center gap-2" data-id="element-3702">
                <BookOpen className="w-4 h-4 text-blue-500" data-id="element-3703" />
                <span className="text-sm font-medium text-gray-700" data-id="element-3704">
                  Vragenbank
                </span>
              </div>
              <span className="text-lg font-bold text-gray-900" data-id="element-3705">
                {contentStats.totalQuestions.toLocaleString('nl')}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-3706">
              <div className="flex items-center gap-2" data-id="element-3707">
                <Tag className="w-4 h-4 text-purple-500" data-id="element-3708" />
                <span className="text-sm font-medium text-gray-700" data-id="element-3709">
                  Families
                </span>
              </div>
              <span className="text-sm text-gray-600" data-id="element-3710">
                {contentStats.totalFamilies} / {contentStats.taxonomyFamilies}
              </span>
            </div>
            <ProgressBar value={contentStats.coveragePct} color={contentStats.coveragePct >= 80 ? 'bg-emerald-500' : contentStats.coveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label="Seed dekking" data-id="element-3711" />
            <ProgressBar value={contentStats.visualCoveragePct} color={contentStats.visualCoveragePct >= 80 ? 'bg-emerald-500' : contentStats.visualCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label={`Visuele componenten (${contentStats.usedVisualsCount}/${contentStats.registeredVisuals})`} data-id="element-3712" />
          </div>

          {/* Right: Quality & Validation */}
          <div className="space-y-4" data-id="element-3713">
            <div className="flex items-center justify-between" data-id="element-3714">
              <div className="flex items-center gap-3" data-id="element-3715">
                {avgScore !== null ? <QualityRing score={avgScore} data-id="element-3716" /> : <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" data-id="element-3717" />}
                <div data-id="element-3718">
                  <div className="text-sm font-medium text-gray-700" data-id="element-3719">
                    Kwaliteitsscore
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-3720">
                    alle {contentStats.totalQuestions.toLocaleString('nl')}{' '}
                    vragen
                  </div>
                </div>
              </div>
              <button onClick={handleRefreshPipeline} disabled={pipelineLoading} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" title="Validatie vernieuwen" data-id="element-3721">
                <RefreshCw className={`w-4 h-4 ${pipelineLoading ? 'animate-spin' : ''}`} data-id="element-3722" />
              </button>
            </div>

            {pipelineLoading ? <div className="flex gap-4" data-id="element-3723">
                {['Geslaagd', 'Warnings', 'Fouten'].map(l => <div key={l} className="flex-1 bg-gray-50 rounded-lg p-2 animate-pulse" data-id="element-3724">
                    <div className="h-5 bg-gray-200 rounded mb-1" data-id="element-3725" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" data-id="element-3726" />
                  </div>)}
              </div> : passCount !== null ? <>
                <div className="flex gap-3" data-id="element-3727">
                  <div className="flex-1 bg-emerald-50 rounded-lg p-2.5 text-center" data-id="element-3728">
                    <div className="text-lg font-bold text-emerald-700" data-id="element-3729">
                      {passCount}
                    </div>
                    <div className="text-xs text-emerald-600" data-id="element-3730">Geslaagd</div>
                  </div>
                  <div className="flex-1 bg-amber-50 rounded-lg p-2.5 text-center" data-id="element-3731">
                    <div className="text-lg font-bold text-amber-700" data-id="element-3732">
                      {warnCount}
                    </div>
                    <div className="text-xs text-amber-600" data-id="element-3733">Warnings</div>
                  </div>
                  <div className="flex-1 bg-red-50 rounded-lg p-2.5 text-center" data-id="element-3734">
                    <div className="text-lg font-bold text-red-700" data-id="element-3735">
                      {failCount}
                    </div>
                    <div className="text-xs text-red-600" data-id="element-3736">Fouten</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-3737">
                  <div className="h-full bg-emerald-500" style={{
                width: `${totalValidated > 0 ? (passCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-3738" />
                  <div className="h-full bg-amber-400" style={{
                width: `${totalValidated > 0 ? (warnCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-3739" />
                  <div className="h-full bg-red-500" style={{
                width: `${totalValidated > 0 ? (failCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-3740" />
                </div>
              </> : <div className="text-sm text-gray-400 text-center py-4" data-id="element-3741">
                Validatie wordt geladen...
              </div>}
          </div>
        </div>
      </motion.div>

      {/* Section 3: Activity & System Health */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6" data-id="element-3742">
        {/* Recent Activity */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-3743">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3744">
            <Clock className="w-4 h-4" data-id="element-3745" /> Recente Activiteit
          </h3>
          <div className="space-y-3" data-id="element-3746">
            {recentActivity.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-3747">
                <div className={`mt-0.5 flex-shrink-0 ${item.type === 'success' ? 'text-emerald-500' : item.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-3748">
                  {item.type === 'success' ? <CheckCircle2 className="w-4 h-4" data-id="element-3749" /> : item.type === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-3750" /> : <Activity className="w-4 h-4" data-id="element-3751" />}
                </div>
                <div className="min-w-0 flex-1" data-id="element-3752">
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-3753">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono" data-id="element-3754">
                    {item.time}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* System Health */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-3755">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3756">
            <Server className="w-4 h-4" data-id="element-3757" /> Systeemgezondheid
          </h3>
          <div className="space-y-4" data-id="element-3758">
            <div className="flex items-center justify-between" data-id="element-3759">
              <span className="text-sm text-gray-600" data-id="element-3760">Strict Mode</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${strictModeOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-3761">
                {strictModeOn ? 'AAN' : 'UIT'}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-3762">
              <span className="text-sm text-gray-600" data-id="element-3763">Gem. responstijd</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-3764">
                {systemHealth.responseTime}ms
              </span>
            </div>
            <div data-id="element-3765">
              <div className="flex items-center justify-between mb-1.5" data-id="element-3766">
                <span className="text-sm text-gray-600" data-id="element-3767">Opslag</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-3768">
                  {systemHealth.storageMb} / 5 MB
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-3769">
                <div className="h-full bg-violet-500 rounded-full" style={{
                width: `${systemHealth.storagePct}%`
              }} data-id="element-3770" />
              </div>
            </div>
            <div data-id="element-3771">
              <div className="flex items-center justify-between mb-1.5" data-id="element-3772">
                <span className="text-sm text-gray-600" data-id="element-3773">Actief deze week</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-3774">
                  {stats.activeThisWeek} leerlingen
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-3775">
                <div className="h-full bg-blue-500 rounded-full" style={{
                width: `${Math.min(100, stats.activeThisWeek / Math.max(stats.totalLearners, 1) * 100)}%`
              }} data-id="element-3776" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Section 4: Quick Insights */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-3777">
        {/* Subscriptions */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-3778">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-3779">
            Abonnementen
          </h3>
          <div className="space-y-2.5" data-id="element-3780">
            {stats.planDistribution.map(item => <div key={item.label} data-id="element-3781">
                <div className="flex justify-between text-xs mb-1" data-id="element-3782">
                  <span className="text-gray-600" data-id="element-3783">{item.label}</span>
                  <span className="font-semibold text-gray-800" data-id="element-3784">
                    {item.count}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-3785">
                  <div className={`h-full rounded-full ${item.color}`} style={{
                width: `${item.pct}%`
              }} data-id="element-3786" />
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Top Learners */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-3787">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-3788">
            <TrendingUp className="w-3.5 h-3.5" data-id="element-3789" /> Top Leerlingen
          </h3>
          {stats.topLearners.length > 0 ? <div className="space-y-2.5" data-id="element-3790">
              {stats.topLearners.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-3791">
                  <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-3792">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3793">
                    <p className="text-xs font-medium text-gray-800 truncate" data-id="element-3794">
                      {item.name}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-violet-600" data-id="element-3795">
                    {item.xp.toLocaleString('nl-NL')} XP
                  </span>
                </div>)}
            </div> : <p className="text-xs text-gray-400" data-id="element-3796">Geen leerlingen gevonden</p>}
        </motion.div>

        {/* Calibration Progress */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-3797">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-3798">
            <ShieldCheck className="w-3.5 h-3.5" data-id="element-3799" /> Kalibratie
          </h3>
          <div className="space-y-3" data-id="element-3800">
            <div className="flex items-end justify-between" data-id="element-3801">
              <span className="text-2xl font-bold text-gray-900" data-id="element-3802">
                {contentStats.calibration.goldStandardPct}%
              </span>
              <span className="text-xs text-gray-500" data-id="element-3803">
                {contentStats.calibration.familiesAtGoldStandard} /{' '}
                {contentStats.calibration.totalFamilies} goud
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-3804">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${contentStats.calibration.goldStandardPct}%`
            }} transition={{
              duration: 1,
              delay: 0.3
            }} className={`h-full rounded-full ${contentStats.calibration.goldStandardPct >= 80 ? 'bg-emerald-500' : contentStats.calibration.goldStandardPct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-3805" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-3806">
              <div className="flex justify-between" data-id="element-3807">
                <span className="text-gray-500" data-id="element-3808">citoDifficulty</span>
                <span className="font-semibold text-gray-700" data-id="element-3809">
                  {contentStats.calibration.citoDifficultyCoveragePct}%
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3810">
                <span className="text-gray-500" data-id="element-3811">solutionSteps</span>
                <span className="font-semibold text-gray-700" data-id="element-3812">
                  {contentStats.calibration.solutionStepsCoveragePct}%
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </PageLayout>;
}